import { useEffect, useState } from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export type PreviewContext = "saved" | "unsaved" | "history";

interface Props {
  open: boolean;
  content: string;
  context: PreviewContext;
  onClose: () => void;
  onDownload: () => Promise<void>;
  onSaveAndDownload: () => Promise<void>;
  onRestore?: () => void;
}

export default function PreviewModal({
  open,
  content,
  context,
  onClose,
  onDownload,
  onSaveAndDownload,
  onRestore,
}: Props) {
  // After "Save and Download PDF" completes, transition unsaved → saved
  const [effectiveContext, setEffectiveContext] = useState<PreviewContext>(context);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setEffectiveContext(context);
      setPdfError(null);
      setSaving(false);
    }
  }, [open, context]);

  async function handleSaveAndDownload() {
    setSaving(true);
    setPdfError(null);
    try {
      await onSaveAndDownload();
      setEffectiveContext("saved");
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : "Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDownload() {
    setPdfError(null);
    try {
      await onDownload();
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : "Download failed.");
    }
  }

  return (
    <Dialog
      open={open}
      onClose={() => {
        // No-op — modal only closes via Close button
      }}
      className="fixed inset-0 z-50"
      aria-label="preview"
    >
      <div className="fixed inset-0 bg-black/60" aria-hidden="true" />

      <div className="fixed inset-0 flex flex-col bg-white dark:bg-slate-900">
        <DialogPanel className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-3 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex-shrink-0">
            <DialogTitle className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Preview
            </DialogTitle>

            <div className="flex items-center gap-2">
              {pdfError && (
                <span className="text-sm text-red-600 dark:text-red-400">{pdfError}</span>
              )}

              {effectiveContext === "history" && onRestore && (
                <button
                  type="button"
                  onClick={onRestore}
                  className="px-3 py-1.5 text-sm font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
                >
                  Restore
                </button>
              )}

              {effectiveContext === "unsaved" ? (
                <button
                  type="button"
                  onClick={handleSaveAndDownload}
                  disabled={saving}
                  className="px-3 py-1.5 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
                >
                  {saving ? "Saving…" : "Save and Download PDF"}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleDownload}
                  className="px-3 py-1.5 text-sm font-medium rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors duration-150"
                >
                  Download PDF
                </button>
              )}

              <button
                type="button"
                onClick={onClose}
                className="px-3 py-1.5 text-sm font-medium rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors duration-150"
              >
                Close
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6">
            <div className="max-w-3xl mx-auto pdf-preview">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
